import { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useSearchParams } from "react-router-dom";
import worksheets from "../data/worksheets.json";

const withCacheBust = (src) => {
  if (!src) return src;
  return src.includes("?") ? `${src}&v=3` : `${src}?v=3`;
};

function WorksheetsList(){
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const classes = ["All Classes", "LKG", "UKG", "I Class", "II Class", "III Class", "IV Class", "V Class", "VI Class", "VII Class"];
  const subjects = ["All Subjects", "Maths", "Science", "English", "Tracing", "Coloring", "Matching", "Puzzle"];
  const paramClass = searchParams.get("grade");
  const paramSubject = searchParams.get("subject");
  const [selectedClass, setSelectedClass] = useState(
    classes.includes(paramClass) ? paramClass : "All Classes"
  );
  const [selectedSubject, setSelectedSubject] = useState(
    subjects.includes(paramSubject) ? paramSubject : "All Subjects"
  );
  const [preview, setPreview] = useState(null);

  const classToRange = (c) => {
    if (c === "LKG") return { min: 0, max: 9 };
    if (c === "UKG") return { min: 0, max: 15 };
    if (c === "I Class") return { min: 0, max: 20 };
    if (c === "II Class") return { min: 0, max: 50 };
    if (c === "III Class") return { min: 0, max: 100 };
    if (c === "IV Class") return { min: 0, max: 200 };
    if (c === "V Class") return { min: 0, max: 500 };
    if (c === "VI Class") return { min: 0, max: 1000 };
    if (c === "VII Class") return { min: 0, max: 1000 };
    return { min: 0, max: 20 };
  };

  const randomInt = (min, max) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  const buildQuestions = (type, c, count = 20) => {
    const { min, max } = classToRange(c);
    const lines = [];
    for (let i = 0; i < count; i += 1) {
      let a = randomInt(min, max);
      let b = randomInt(min, max);
      if (type === "subtraction" && b > a) {
        const t = a;
        a = b;
        b = t;
      }
      const op = type === "addition" ? "+" : "-";
      lines.push(`${a} ${op} ${b} = `);
    }
    return lines;
  };

  const downloadTextWorksheet = (type, index, questionsOverride) => {
    const title = `${type === "addition" ? "Addition" : "Subtraction"} Worksheet`;
    const questions = questionsOverride || buildQuestions(type, selectedClass, 20);
    const content = [
      `${title} - ${selectedClass}`,
      `Subject: Maths`,
      `Sheet: ${index + 1}`,
      "",
      ...questions
    ].join("\n");

    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${selectedClass.replace(" ", "-").toLowerCase()}-${type}-sheet-${index + 1}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const gradeToClass = (grade) => {
    if (grade === "LKG") return "LKG";
    if (grade === "UKG") return "UKG";
    if (grade === "1") return "I Class";
    if (grade === "2") return "II Class";
    if (grade === "3") return "III Class";
    if (grade === "4") return "IV Class";
    if (grade === "5") return "V Class";
    if (grade === "6") return "VI Class";
    if (grade === "7") return "VII Class";
    return grade;
  };

  const subjectToLabel = (subject) => {
    if (!subject) return "";
    const s = subject.toLowerCase();
    if (s === "math" || s === "maths") return "Maths";
    if (s === "science") return "Science";
    if (s === "english") return "English";
    if (s === "tracing") return "Tracing";
    if (s === "coloring") return "Coloring";
    if (s === "matching") return "Matching";
    if (s === "puzzle") return "Puzzle";
    return subject;
  };

  const filtered = worksheets.filter((w) => {
    const c = gradeToClass(String(w.grade));
    const s = subjectToLabel(String(w.subject));
    const classOk = selectedClass === "All Classes" ? true : c === selectedClass;
    const subjectOk = selectedSubject === "All Subjects" ? true : s === selectedSubject;
    return classOk && subjectOk;
  });

  const dynamicMathClasses = ["I Class", "II Class"];
  const showDynamicMath =
    selectedSubject === "Maths" && dynamicMathClasses.includes(selectedClass);

  const mathSheets =
    showDynamicMath
      ? [
          ...Array.from({ length: 10 }).map((_, i) => ({
            id: `add-${i + 1}`,
            type: "addition",
            label: `Addition Sheet ${i + 1}`
          })),
          ...Array.from({ length: 10 }).map((_, i) => ({
            id: `sub-${i + 1}`,
            type: "subtraction",
            label: `Subtraction Sheet ${i + 1}`
          }))
        ]
      : [];

  const previewContent = useMemo(() => {
    if (!preview) return null;
    if (preview.kind === "static") {
      return {
        title: preview.title,
        image: preview.image,
        download: preview.download
      };
    }
    const title = `${preview.type === "addition" ? "Addition" : "Subtraction"} Worksheet`;
    return {
      title,
      questions: preview.questions
    };
  }, [preview]);

return(
<>
<div
  style={{
    minHeight: "100vh",
    padding: "24px",
    background: "linear-gradient(160deg, #FFF1C7 0%, #E8F2FF 45%, #FFE6F3 100%)",
    backgroundImage: "radial-gradient(circle at 12% 8%, rgba(255,255,255,0.9) 0, rgba(255,255,255,0) 45%), radial-gradient(circle at 88% 12%, rgba(255,239,210,0.9) 0, rgba(255,239,210,0) 40%)",
    fontFamily: '"Baloo 2", "Comic Sans MS", "Trebuchet MS", sans-serif'
  }}
>

<Helmet>
  <title>Worksheets – PracticeNest</title>
  <meta
    name="description"
    content="Browse printable worksheets by class and subject. Free practice sheets for kids from LKG to Class 7."
  />
  <link rel="canonical" href="https://practicenest.in/worksheets" />
  <link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
  <link href="https://fonts.googleapis.com/css2?family=Baloo+2:wght@500;700;800&display=swap" rel="stylesheet" />
</Helmet>

<div style={{maxWidth: "980px", margin: "0 auto"}}>

<div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px"}}>
  <button
    onClick={() => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/");
      }
    }}
    style={{
      padding: "8px 12px",
      borderRadius: "10px",
      border: "1px solid #333",
      background: "#fff",
      cursor: "pointer"
    }}
  >
    ← Back
  </button>

  <span style={{display: "flex", gap: "12px", alignItems: "center"}}>
    <a href="/" style={{textDecoration: "none", fontWeight: "600"}}>Home</a>
  </span>
</div>

<div
  style={{
    position: "relative",
    background: "linear-gradient(135deg, #FFFFFF 0%, #F4FBFF 100%)",
    borderRadius: "22px",
    padding: "22px",
    boxShadow: "0 18px 34px rgba(0,0,0,0.1)",
    border: "1px solid rgba(0,0,0,0.06)",
    marginBottom: "22px",
    overflow: "hidden"
  }}
>
  <div style={{position: "absolute", top: "-20px", right: "-10px", width: "90px", height: "90px", background: "#FFD34E", borderRadius: "50%", opacity: 0.5}} />
  <div style={{position: "absolute", bottom: "-30px", left: "-20px", width: "120px", height: "120px", background: "#B8FFCB", borderRadius: "50%", opacity: 0.5}} />

  <div style={{fontSize: "12px", letterSpacing: "1px", textTransform: "uppercase", color: "#3A6EA5"}}>
    PracticeNest
  </div>
  <h1 style={{fontSize: "36px", margin: "6px 0 6px"}}>Worksheets Library</h1>
  <p style={{marginTop: 0, marginBottom: "12px"}}>
    Pick a class and subject, then download a worksheet instantly.
  </p>
  <div style={{display: "flex", gap: "8px", flexWrap: "wrap"}}>
    <span style={{padding: "6px 12px", background: "#FFD34E", borderRadius: "999px", fontSize: "12px", fontWeight: "700"}}>
      {selectedClass}
    </span>
    <span style={{padding: "6px 12px", background: "#B8FFCB", borderRadius: "999px", fontSize: "12px", fontWeight: "700"}}>
      {selectedSubject}
    </span>
    <span style={{padding: "6px 12px", background: "#DDEBFF", borderRadius: "999px", fontSize: "12px", fontWeight: "700"}}>
      🎈 Kid‑Friendly
    </span>
  </div>
</div>

<div style={{display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "24px"}}>
  <div id="grades" style={{background: "#fff", padding: "14px", borderRadius: "16px", boxShadow: "0 10px 22px rgba(0,0,0,0.08)", border: "2px solid #FFE08A"}}>
    <div style={{fontWeight: "800", marginBottom: "8px"}}>1. Choose Class 🏫</div>
    <div style={{display: "flex", gap: "8px", flexWrap: "wrap"}}>
      {classes.map((c) => (
        <button
          key={c}
          onClick={() => {
            setSelectedClass(c);
            const next = new URLSearchParams(searchParams);
            if (c === "All Classes") {
              next.delete("grade");
            } else {
              next.set("grade", c);
            }
            setSearchParams(next);
          }}
          style={{
            padding: "8px 12px",
            borderRadius: "999px",
            border: c === selectedClass ? "2px solid #111" : "1px solid #bbb",
            background: c === selectedClass ? "#FFD34E" : "#fff",
            cursor: "pointer"
          }}
        >
          {c}
        </button>
      ))}
    </div>
  </div>

  <div id="subjects" style={{background: "#fff", padding: "14px", borderRadius: "16px", boxShadow: "0 10px 22px rgba(0,0,0,0.08)", border: "2px solid #BFF0D2"}}>
    <div style={{fontWeight: "800", marginBottom: "8px"}}>2. Choose Subject 🎯</div>
    <div style={{display: "flex", gap: "8px", flexWrap: "wrap"}}>
      {subjects.map((s) => (
        <button
          key={s}
          onClick={() => {
            setSelectedSubject(s);
            const next = new URLSearchParams(searchParams);
            if (s === "All Subjects") {
              next.delete("subject");
            } else {
              next.set("subject", s);
            }
            setSearchParams(next);
          }}
          style={{
            padding: "8px 12px",
            borderRadius: "999px",
            border: s === selectedSubject ? "2px solid #111" : "1px solid #bbb",
            background: s === selectedSubject ? "#B8FFCB" : "#fff",
            cursor: "pointer"
          }}
        >
          {s}
        </button>
      ))}
    </div>
  </div>
</div>

<div style={{fontWeight: "800", marginBottom: "10px"}}>3. Worksheets 🎒</div>
<ul style={{listStyle:"none", padding:0, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px"}}>

{selectedSubject === "Maths" && selectedClass === "All Classes" ? (
  <li style={{background: "#fff", borderRadius: "16px", padding: "16px", boxShadow: "0 10px 22px rgba(0,0,0,0.08)", borderTop: "4px solid #2D7FF9"}}>
    <div style={{fontSize: "13px", color: "#555", marginBottom: "6px"}}>
      Select a class to generate Maths sheets.
    </div>
    <h3 style={{marginTop: 0}}>Pick a class above</h3>
  </li>
) : showDynamicMath ? (
  <>
    <li style={{gridColumn: "1 / -1", fontWeight: "700", color: "#2D7FF9"}}>
      Chapter 1: Addition – Addition Sheets
    </li>
    {mathSheets
      .filter((s) => s.type === "addition")
      .map((s, i)=>(
        <li key={s.id} style={{background: "#fff", borderRadius: "16px", padding: "16px", boxShadow: "0 10px 22px rgba(0,0,0,0.08)", borderTop: "4px solid #2D7FF9"}}>
          <div style={{fontSize: "13px", color: "#555", marginBottom: "6px"}}>
            {selectedClass} • Maths
          </div>
          <h3 style={{marginTop: 0}}>➕ {s.label}</h3>
          <button
            onClick={() =>
              setPreview({
                type: s.type,
                index: i % 10,
                questions: buildQuestions(s.type, selectedClass, 20)
              })
            }
            style={{
              display:"inline-block",
              padding:"10px 14px",
              fontSize:"14px",
              fontWeight: "600",
              background: "#2D7FF9",
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              cursor: "pointer"
            }}
          >
            Preview & Download
          </button>
        </li>
      ))}
    <li style={{gridColumn: "1 / -1", fontWeight: "700", color: "#FF8A3D"}}>
      Chapter 2: Subtraction – Subtraction Sheets
    </li>
    {mathSheets
      .filter((s) => s.type === "subtraction")
      .map((s, i)=>(
        <li key={s.id} style={{background: "#fff", borderRadius: "16px", padding: "16px", boxShadow: "0 10px 22px rgba(0,0,0,0.08)", borderTop: "4px solid #FF8A3D"}}>
          <div style={{fontSize: "13px", color: "#555", marginBottom: "6px"}}>
            {selectedClass} • Maths
          </div>
          <h3 style={{marginTop: 0}}>- {s.label}</h3>
          <button
            onClick={() =>
              setPreview({
                type: s.type,
                index: i % 10,
                questions: buildQuestions(s.type, selectedClass, 20)
              })
            }
            style={{
              display:"inline-block",
              padding:"10px 14px",
              fontSize:"14px",
              fontWeight: "600",
              background: "#2D7FF9",
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              cursor: "pointer"
            }}
          >
            Preview & Download
          </button>
        </li>
      ))}
  </>
) : (
  filtered.map((w)=>(
    <li key={w.id} style={{background: "#fff", borderRadius: "16px", padding: "16px", boxShadow: "0 10px 22px rgba(0,0,0,0.08)", borderTop: "4px solid #7B6CF6"}}>
      <div style={{fontSize: "13px", color: "#555", marginBottom: "6px"}}>
        {gradeToClass(String(w.grade))} • {subjectToLabel(String(w.subject))}
      </div>
      <h3 style={{marginTop: 0}}>{w.title}</h3>
      <button
        onClick={() =>
          setPreview({
            kind: "static",
            title: w.title,
            image: w.preview,
            download: w.file
          })
        }
        style={{
          display:"inline-block",
          padding:"10px 14px",
          fontSize:"14px",
          fontWeight: "600",
          background: "#2D7FF9",
          color: "#fff",
          borderRadius: "10px",
          textDecoration: "none",
          border: "none",
          cursor: "pointer"
        }}
      >
        Preview & Download
      </button>
    </li>
  ))
)}

</ul>

</div>
</div>

{previewContent && (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.45)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "20px",
      zIndex: 50
    }}
  >
    <div
      style={{
        width: "100%",
        maxWidth: "720px",
        background: "#fff",
        borderRadius: "16px",
        padding: "20px",
        boxShadow: "0 16px 30px rgba(0,0,0,0.2)"
      }}
    >
      <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
        <div>
          <div style={{fontSize: "12px", color: "#666"}}>
            {preview.kind === "static" ? "Worksheet Preview" : `${selectedClass} • Maths`}
          </div>
          <h2 style={{margin: "6px 0"}}>{previewContent.title}</h2>
        </div>
        <button
          onClick={() => setPreview(null)}
          style={{
            border: "1px solid #bbb",
            background: "#fff",
            borderRadius: "8px",
            padding: "6px 10px",
            cursor: "pointer"
          }}
        >
          Close
        </button>
      </div>

      <div
        style={{
          marginTop: "12px",
          padding: "12px",
          background: "#F7F7F7",
          borderRadius: "10px",
          maxHeight: "50vh",
          overflow: "auto",
          fontFamily: "monospace",
          fontSize: "14px",
          whiteSpace: "pre-wrap"
        }}
      >
        {previewContent.image ? (
          <img
            src={previewContent.image}
            alt="worksheet preview"
            style={{width: "100%", height: "auto", display: "block"}}
          />
        ) : (
          previewContent.questions.map((q, idx) => (
            <div key={idx}>{q}</div>
          ))
        )}
      </div>

      <div style={{marginTop: "14px", display: "flex", gap: "10px"}}>
        {preview.kind === "static" ? (
          <a
            href={withCacheBust(previewContent.download)}
            download
            style={{
              padding:"10px 14px",
              fontSize:"14px",
              fontWeight: "600",
              background: "#2D7FF9",
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              cursor: "pointer",
              textDecoration: "none"
            }}
          >
            Download
          </a>
        ) : (
          <>
            <button
              onClick={() => {
                downloadTextWorksheet(preview.type, preview.index, preview.questions);
                setPreview(null);
              }}
              style={{
                padding:"10px 14px",
                fontSize:"14px",
                fontWeight: "600",
                background: "#2D7FF9",
                color: "#fff",
                border: "none",
                borderRadius: "10px",
                cursor: "pointer"
              }}
            >
              Download
            </button>
            <button
              onClick={() =>
                setPreview({
                  ...preview,
                  questions: buildQuestions(preview.type, selectedClass, 20)
                })
              }
              style={{
                padding:"10px 14px",
                fontSize:"14px",
                fontWeight: "600",
                background: "#FFD34E",
                color: "#111",
                border: "none",
                borderRadius: "10px",
                cursor: "pointer"
              }}
            >
              Regenerate
            </button>
          </>
        )}
      </div>
    </div>
  </div>
)}
</>
)

}

export default WorksheetsList;
